import React, { useCallback } from 'react';
import { TabState, PaneNode, TabContainer as TabContainerType } from '../../types/pane.types';
import { MIDASProject, createDatasetId, createReportId } from '../../types';
import { 
  createDataTableTab, 
  createStatisticsTab, 
  createReportTab,
  createSelectedRowsTab
} from '../tab-system/TabRegistry';
import { findActiveContainer, findSiblingPane, findActiveTab } from '../utils/paneUtils';
import logger from '../utils/logger';

/**
 * タブ操作に関する機能を管理するフック
 * 
 * 責務:
 * - タブの作成・追加
 * - 修飾キーに応じたタブの開き方制御
 * - 特定タイプのタブを開く専用メソッド
 * - タブのドラッグ&ドロップ
 */
interface UseTabOperationsProps {
  project: MIDASProject | null;
  layout: {
    rootPane: PaneNode;
    activeContainerId: string | null | undefined;
  };
  addTab: (containerId: string, tab: TabState) => void;
  addTabWithOptions: (containerId: string, tab: TabState, options: { activatePane?: boolean }) => void;
  closeTab: (containerId: string, tabId: string) => void;
  moveTabWithinPane: (containerId: string, fromIndex: number, toIndex: number) => void;
  moveTabToPane: (tabId: string, sourceContainerId: string, targetContainerId: string, dropIndex?: number) => void;
  startDrag: (tabId: string, containerId: string, rootPane: PaneNode) => void;
  endDrag: () => void;
}

export function useTabOperations({
  project,
  layout,
  addTab,
  addTabWithOptions,
  closeTab,
  moveTabWithinPane,
  moveTabToPane,
  startDrag,
  endDrag
}: UseTabOperationsProps) {
  
  /**
   * アクティブコンテナIDを取得
   */
  const getActiveContainerId = useCallback(() => {
    return findActiveContainer(layout.rootPane, layout.activeContainerId ?? null);
  }, [layout.rootPane, layout.activeContainerId]);
  
  /**
   * 兄弟ペインIDを取得
   */
  const getSiblingPaneId = useCallback((containerId: string) => {
    return findSiblingPane(layout.rootPane, containerId);
  }, [layout.rootPane]);

  /**
   * タブを開くための共通ヘルパー関数
   * 修飾キーに応じてタブの開き方を制御する
   * 
   * 修飾キーの動作:
   * - 通常クリック/Enter: アクティブペインにタブを開き、そのペインをアクティブ化
   * - Shift+クリック/Shift+Enter: 兄弟ペインにタブを開き、アクティブペインは変更しない
   *   (兄弟ペインがない場合はアクティブペインに開く)
   */
  const openTabWithModifier = useCallback((tab: TabState, event?: React.MouseEvent | React.KeyboardEvent) => {
    const activeContainerId = getActiveContainerId();
    if (!activeContainerId) {
      logger.warn('[useTabOperations] アクティブなコンテナが見つかりません');
      return;
    }
    
    // Shiftキーが押されている場合は兄弟ペインに開く
    const openInSibling = event?.shiftKey === true;
    
    
    if (openInSibling) {
      const siblingPaneId = getSiblingPaneId(activeContainerId);
      
      if (siblingPaneId) {
        // 兄弟ペインに開く場合は、ペインをアクティブ化しない
        addTabWithOptions(siblingPaneId, tab, { activatePane: false });
      } else {
        // 兄弟ペインがない場合は通常通りアクティブペインに開く
        addTab(activeContainerId, tab);
      }
    } else {
      addTab(activeContainerId, tab);
    }
  }, [getActiveContainerId, getSiblingPaneId, addTab, addTabWithOptions]);

  /**
   * データセットタブを開く
   */
  const openDatasetTab = useCallback((datasetId: string, event?: React.MouseEvent | React.KeyboardEvent) => {
    const datasetIdTyped = createDatasetId(datasetId);
    if (!project?.datasets?.[datasetIdTyped] || !layout.rootPane) {
      logger.warn('[useTabOperations] データセットが見つかりません:', {
        datasetId,
        availableDatasets: project?.datasets ? Object.keys(project.datasets) : []
      });
      return;
    }
    
    const activeContainerId = getActiveContainerId();
    if (!activeContainerId) {
      logger.warn('[useTabOperations] アクティブなコンテナが見つかりません');
      return;
    }
    
    const dataset = project.datasets[datasetIdTyped];
    const dataTableTab = createDataTableTab(datasetIdTyped, dataset.name);
    openTabWithModifier(dataTableTab, event);
  }, [project?.datasets, layout.rootPane, openTabWithModifier, getActiveContainerId]);

  /**
   * レポートタブを開く
   */
  const openReportTab = useCallback((reportId?: string, event?: React.MouseEvent | React.KeyboardEvent) => {
    if (!layout.rootPane) return;
    
    // レポートIDが指定されていない場合は、利用可能なレポートの最初のものを使用
    const availableReports = project?.reports || [];
    const targetReportId = reportId || (availableReports.length > 0 ? availableReports[0].id : 'sample-report');
    const reportTitle = availableReports.find(r => r.id === targetReportId)?.name || 'Report';
    
    const reportTab = createReportTab(createReportId(targetReportId), reportTitle);
    openTabWithModifier(reportTab, event);
  }, [layout.rootPane, openTabWithModifier, project?.reports]);

  /**
   * 統計タブを開く
   */
  const openStatisticsTab = useCallback((event?: React.MouseEvent | React.KeyboardEvent) => {
    if (!layout.rootPane) return;
    
    const statisticsTab = createStatisticsTab();
    openTabWithModifier(statisticsTab, event);
  }, [layout.rootPane, openTabWithModifier]);

  /**
   * 選択行タブを開く
   */
  const openSelectedRowsTab = useCallback((datasetId: string, event?: React.MouseEvent | React.KeyboardEvent) => {
    const datasetIdTyped = createDatasetId(datasetId);
    if (!layout.rootPane || !project?.datasets?.[datasetIdTyped]) return;
    
    const selectedRowsTab = createSelectedRowsTab(datasetIdTyped);
    openTabWithModifier(selectedRowsTab, event);
  }, [layout.rootPane, project?.datasets, openTabWithModifier]);

  /**
   * タブをコンテナ間で移動する際のドロップハンドラー
   */
  const handleTabDrop = useCallback((draggedTabId: string, targetContainerId: string, dropIndex?: number) => {
    // タブが元々所属していたコンテナを見つける
    const findContainerForTab = (node: PaneNode, tabId: string): string | null => {
      if (node.type === 'container') {
        const hasTab = node.tabs.some((tab: TabState) => tab.id === tabId);
        if (hasTab) return node.id;
      } else if (node.type === 'split') {
        const leftResult = findContainerForTab(node.leftChild, tabId);
        if (leftResult) return leftResult;
        
        const rightResult = findContainerForTab(node.rightChild, tabId);
        if (rightResult) return rightResult;
      }
      return null;
    };

    const sourceContainerId = findContainerForTab(layout.rootPane, draggedTabId);
    
    if (sourceContainerId && targetContainerId === sourceContainerId) {
      // 同じペイン内での移動
      const findContainerNode = (node: PaneNode, containerId: string): TabContainerType | null => {
        if (node.type === 'container' && node.id === containerId) {
          return node;
        } else if (node.type === 'split') {
          const leftResult = findContainerNode(node.leftChild, containerId);
          if (leftResult) return leftResult;
          
          const rightResult = findContainerNode(node.rightChild, containerId);
          if (rightResult) return rightResult;
        }
        return null;
      };

      const containerNode = findContainerNode(layout.rootPane, sourceContainerId);
      if (containerNode) {
        const draggedTabIndex = containerNode.tabs.findIndex((tab: TabState) => tab.id === draggedTabId);
        if (draggedTabIndex >= 0 && dropIndex !== undefined && draggedTabIndex !== dropIndex) {
          moveTabWithinPane(sourceContainerId, draggedTabIndex, dropIndex);
        }
      }
    } else if (sourceContainerId && targetContainerId !== sourceContainerId) {
      // 異なるペイン間での移動
      moveTabToPane(draggedTabId, sourceContainerId, targetContainerId, dropIndex);
    }
  }, [layout.rootPane, moveTabWithinPane, moveTabToPane]);

  /**
   * タブドラッグ開始ハンドラー
   */
  const handleTabDragStart = useCallback((tabId: string, containerId: string, _event: React.DragEvent) => {
    
    startDrag(tabId, containerId, layout.rootPane);
  }, [layout.rootPane, startDrag]);

  /**
   * タブドラッグ終了ハンドラー
   */
  const handleTabDragEnd = useCallback(() => {
    endDrag();
  }, [endDrag]);

  /**
   * アクティブタブを閉じる
   */
  const closeActiveTab = useCallback(() => {
    const activeTab = layout.activeContainerId ? findActiveTab(layout.rootPane, layout.activeContainerId) : null;
    if (activeTab && layout.activeContainerId) {
      closeTab(layout.activeContainerId, activeTab.id);
    }
  }, [layout.rootPane, layout.activeContainerId, closeTab]);

  /**
   * アクティブタブの情報を取得
   */
  const getActiveTabInfo = useCallback(() => {
    if (!layout.activeContainerId) return null;
    
    const activeTab = findActiveTab(layout.rootPane, layout.activeContainerId);
    if (!activeTab) return null;
    
    return {
      tabId: activeTab.id,
      containerId: layout.activeContainerId,
      tab: activeTab
    };
  }, [layout.rootPane, layout.activeContainerId]);

  return {
    // ヘルパー関数
    getActiveContainerId,
    getSiblingPaneId,
    
    // タブ操作
    openTabWithModifier,
    openDatasetTab,
    openReportTab,
    openStatisticsTab,
    openSelectedRowsTab,
    closeActiveTab,
    
    // ドラッグ&ドロップ
    handleTabDragStart,
    handleTabDragEnd,
    handleTabDrop,
    
    // 情報取得
    getActiveTabInfo
  };
}